import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import { LoaderCircle, Volume2 } from 'lucide-react';
import { Forward10 as Forward10Icon, Pause as PauseIcon, PlayArrow as PlayArrowIcon, Replay10 as Replay10Icon } from '@mui/icons-material';
import { generateAudio, uploadAudioToStorage } from '../../services/ttsService.js';

const getPageText = (page) => page?.text || page?.content || '';
const EMPTY_PAGE_AUDIO_URLS = {};

const getWords = (text) => String(text || '').match(/\S+/g) || [];

const estimateWordIndex = (text, progress) => {
  const words = getWords(text);
  if (!words.length) return -1;
  return Math.min(words.length - 1, Math.floor(Math.max(0, Math.min(0.999, progress)) * words.length));
};

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '00:00';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const getStoredPageUrl = (urls, pageKey, legacyPageNumber = null) => (
  urls?.[pageKey]
  || urls?.[String(pageKey)]
  || (legacyPageNumber !== null ? urls?.[legacyPageNumber] || urls?.[String(legacyPageNumber)] : null)
  || null
);

const NarrationCore = forwardRef(function NarrationCore({
  mode = 'browser',
  pages = [],
  currentPage = 0,
  totalPages = pages.length + 1,
  voice = 'alloy',
  storyId = null,
  pageAudioUrls = EMPTY_PAGE_AUDIO_URLS,
  onPageComplete = null,
  onWordChange = null,
  onPageAudioStored = null,
  getToken = null,
  userId = null,
}, ref) {
  const audioRef = useRef(null);
  const utteranceRef = useRef(null);
  const blobUrlsRef = useRef({});
  const autoStartPageRef = useRef(null);
  const currentPageRef = useRef(currentPage);
  const playCurrentPageRef = useRef(null);
  const browserCharIndexRef = useRef(0);
  const browserCancelingRef = useRef(false);
  const [storedUrls, setStoredUrls] = useState(pageAudioUrls || {});
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const pageAudioUrlsKey = JSON.stringify(pageAudioUrls || EMPTY_PAGE_AUDIO_URLS);
  useEffect(() => {
    const incoming = JSON.parse(pageAudioUrlsKey);
    setStoredUrls((current) => {
      const keys = Object.keys(incoming);
      const unchanged = keys.length === Object.keys(current).length && keys.every((key) => current[key] === incoming[key]);
      return unchanged ? current : { ...incoming, ...current };
    });
  }, [pageAudioUrlsKey]);

  const currentText = getPageText(pages[currentPage - 1]);

  const clearWord = useCallback(() => onWordChange?.(-1), [onWordChange]);

  const stop = useCallback(() => {
    window.speechSynthesis?.cancel();
    browserCancelingRef.current = false;
    utteranceRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onloadedmetadata = null;
      audioRef.current.ontimeupdate = null;
      audioRef.current.onended = null;
    }
    autoStartPageRef.current = null;
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentTime(0);
    browserCharIndexRef.current = 0;
    clearWord();
  }, [clearWord]);

  const getAudioUrl = useCallback(async (pageNumber) => {
    const page = pages[pageNumber - 1];
    const pageKey = page?.audioKey || page?.displayPageId || String(pageNumber);
    const legacyPageNumber = page?.chunkIndex === 0 ? page?.sourcePageNumber : null;
    const persistedUrl = getStoredPageUrl(storedUrls, pageKey, legacyPageNumber)
      || getStoredPageUrl(pageAudioUrls, pageKey, legacyPageNumber);
    if (persistedUrl) return persistedUrl;
    if (!getToken || !voice) throw new Error('Narration is not configured for this story.');

    const text = getPageText(page);
    if (!text) throw new Error('This page has no narration text.');

    setIsLoading(true);
    try {
      const token = await getToken();
      const audioContent = await generateAudio(text, voice, token);
      const objectUrl = URL.createObjectURL(new Blob([audioContent], { type: 'audio/wav' }));
      blobUrlsRef.current[pageKey] = objectUrl;
      setStoredUrls((current) => ({ ...current, [pageKey]: objectUrl }));

      if (storyId && userId) {
        uploadAudioToStorage(audioContent, storyId, voice, userId, pageKey, token)
          .then((downloadUrl) => onPageAudioStored?.(voice, pageKey, downloadUrl))
          .catch((uploadError) => console.warn('Failed to persist page narration:', uploadError.message));
      }
      return objectUrl;
    } finally {
      setIsLoading(false);
    }
  }, [getToken, pageAudioUrls, pages, onPageAudioStored, storedUrls, storyId, userId, voice]);

  const completePage = useCallback((pageNumber) => {
    if (currentPageRef.current !== pageNumber) return;
    utteranceRef.current = null;
    setIsPlaying(false);
    setIsPaused(false);
    clearWord();
    if (pageNumber < totalPages - 1) {
      autoStartPageRef.current = pageNumber + 1;
      onPageComplete?.(pageNumber + 1);
    }
  }, [clearWord, onPageComplete, totalPages]);

  const speakWithBrowser = useCallback((pageNumber, startCharIndex = 0) => {
    const text = getPageText(pages[pageNumber - 1]);
    if (!text || !window.speechSynthesis) {
      setError('Read aloud is not supported in this browser.');
      return;
    }
    const safeStartCharIndex = Math.max(0, Math.min(text.length, startCharIndex));
    const remainingText = text.slice(safeStartCharIndex);
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(remainingText);
    const wordStarts = [];
    const pattern = /\S+/g;
    let match;
    while ((match = pattern.exec(text)) !== null) wordStarts.push(match.index);
    const remainingWordCount = wordStarts.filter((start) => start >= safeStartCharIndex).length;
    const estimatedDuration = duration || Math.max(1, (wordStarts.length / 150) * 60);
    utterance.rate = 0.9;
    utterance.pitch = 1.02;
    setDuration(estimatedDuration);
    setCurrentTime(estimatedDuration * (wordStarts.filter((start) => start < safeStartCharIndex).length / Math.max(1, wordStarts.length - 1)));
    utterance.onboundary = (event) => {
      const charIndex = safeStartCharIndex + (event.charIndex || 0);
      browserCharIndexRef.current = charIndex;
      let index = wordStarts.findIndex((start) => start > charIndex) - 1;
      if (index < 0) index = wordStarts.length - 1;
      onWordChange?.(index);
      setCurrentTime(estimatedDuration * (wordStarts.length > 1 ? index / (wordStarts.length - 1) : 1));
    };
    utterance.onend = () => {
      if (browserCancelingRef.current) return;
      completePage(pageNumber);
    };
    utterance.onerror = () => {
      if (browserCancelingRef.current) return;
      utteranceRef.current = null;
      setIsPlaying(false);
      setIsPaused(false);
      clearWord();
      setError('The browser could not play this narration.');
    };
    utteranceRef.current = utterance;
    browserCancelingRef.current = false;
    setError(null);
    setIsPlaying(true);
    setIsPaused(false);
    if (!remainingWordCount) {
      completePage(pageNumber);
      return;
    }
    window.speechSynthesis.speak(utterance);
  }, [clearWord, completePage, duration, onWordChange, pages]);

  const playWithAudio = useCallback(async (pageNumber) => {
    const audio = audioRef.current;
    if (!audio) return;
    setError(null);
    const url = await getAudioUrl(pageNumber);
    const pageKey = pages[pageNumber - 1]?.audioKey || pages[pageNumber - 1]?.displayPageId || String(pageNumber);
    if (audio.dataset.pageNumber !== String(pageKey) || audio.src !== url) {
      audio.src = url;
      audio.dataset.pageNumber = String(pageKey);
      audio.load();
      setCurrentTime(0);
      await new Promise((resolve, reject) => {
        const onReady = () => { cleanup(); resolve(); };
        const onError = () => { cleanup(); reject(new Error('The page narration could not be loaded.')); };
        const cleanup = () => {
          audio.removeEventListener('canplay', onReady);
          audio.removeEventListener('error', onError);
        };
        audio.addEventListener('canplay', onReady, { once: true });
        audio.addEventListener('error', onError, { once: true });
      });
    }
    setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    audio.onloadedmetadata = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    audio.ontimeupdate = () => {
      if (audio.duration) {
        setDuration(audio.duration);
        setCurrentTime(audio.currentTime);
        onWordChange?.(estimateWordIndex(getPageText(pages[pageNumber - 1]), audio.currentTime / audio.duration));
      }
    };
    audio.onended = () => completePage(pageNumber);
    await audio.play();
    setIsPlaying(true);
    setIsPaused(false);
  }, [completePage, getAudioUrl, onWordChange, pages]);

  const playCurrentPage = useCallback(async () => {
    if (currentPage <= 0 || currentPage >= totalPages) return;
    try {
      if (mode === 'browser') speakWithBrowser(currentPage);
      else await playWithAudio(currentPage);
    } catch (playError) {
      setIsPlaying(false);
      setIsPaused(false);
      clearWord();
      setError(playError.message || 'Narration could not be played.');
    }
  }, [clearWord, currentPage, mode, playWithAudio, speakWithBrowser, totalPages]);

  const toggle = () => {
    if (isLoading) return;
    if (isPlaying) {
      if (mode === 'browser') {
        // Cancelling the current utterance stops speech immediately. Browser
        // pause() can remain audible while the speech queue drains, so keep
        // the last boundary and recreate only the remaining text on resume.
        browserCancelingRef.current = true;
        window.speechSynthesis?.cancel();
      } else audioRef.current?.pause();
      setIsPlaying(false);
      setIsPaused(true);
      return;
    }
    if (isPaused) {
      if (mode === 'browser') speakWithBrowser(currentPage, browserCharIndexRef.current);
      else {
        audioRef.current?.play();
        setIsPlaying(true);
        setIsPaused(false);
      }
      return;
    }
    playCurrentPage();
  };

  const seekTo = (nextTime) => {
    if (mode !== 'tts' || !audioRef.current || !duration) return;
    const clampedTime = Math.max(0, Math.min(duration, nextTime));
    audioRef.current.currentTime = clampedTime;
    setCurrentTime(clampedTime);
    onWordChange?.(estimateWordIndex(getPageText(pages[currentPage - 1]), clampedTime / duration));
  };

  const handleSeek = (_event, value) => seekTo((Number(value) / 100) * duration);
  const handleSkip = (seconds) => seekTo(currentTime + seconds);

  useEffect(() => {
    playCurrentPageRef.current = playCurrentPage;
  }, [playCurrentPage]);

  useImperativeHandle(ref, () => ({ stop, play: playCurrentPage }));

  useEffect(() => {
    const previousPage = currentPageRef.current;
    currentPageRef.current = currentPage;
    if (currentPage === 0) {
      if (isPlaying || isPaused || utteranceRef.current) stop();
      return undefined;
    }
    if (autoStartPageRef.current === currentPage) {
      autoStartPageRef.current = null;
      const timeout = window.setTimeout(() => playCurrentPageRef.current?.(), 760);
      return () => window.clearTimeout(timeout);
    }
    if (previousPage !== currentPage) stop();
    return undefined;
  }, [currentPage, stop]);

  useEffect(() => () => {
    stop();
    Object.values(blobUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
  }, [stop]);

  if (currentPage === 0) return null;

  return (
    <Box className="storybook-3d-reader__narration" aria-label="Page narration controls">
      <audio ref={audioRef} preload="metadata" />
      <Box className="storybook-3d-reader__narration-label">
        <Volume2 size={17} />
        <Box>
          <Typography>Read this page aloud</Typography>
          <small>
            {mode === 'browser'
              ? (isPlaying ? 'Local browser voice is reading' : 'Local browser voice · words highlight on the page')
              : (isPlaying ? 'xAI page narration is reading and turning pages' : 'xAI page narration · words highlight on the page')}
          </small>
        </Box>
      </Box>
      <Box className="storybook-3d-reader__narration-player">
        <Slider
          className="storybook-3d-reader__narration-progress"
          aria-label="Page narration progress"
          value={duration ? (currentTime / duration) * 100 : 0}
          onChange={handleSeek}
          min={0}
          max={100}
          step={0.1}
          disabled={isLoading || mode !== 'tts' || !duration}
        />
        <Box className="storybook-3d-reader__narration-timing">
          <Typography component="span">{formatTime(currentTime)}</Typography>
          <Box className="storybook-3d-reader__narration-actions">
            <IconButton aria-label="Skip backward 10 seconds" onClick={() => handleSkip(-10)} disabled={isLoading || mode !== 'tts' || !duration}>
              <Replay10Icon fontSize="small" />
            </IconButton>
            <IconButton
              className="storybook-3d-reader__narration-play"
              aria-label={isPlaying ? 'Pause page narration' : 'Play page narration'}
              aria-pressed={isPlaying}
              aria-busy={isLoading}
              onClick={toggle}
              disabled={isLoading}
            >
              {isLoading ? <LoaderCircle size={20} className="spin" /> : isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <IconButton aria-label="Skip forward 10 seconds" onClick={() => handleSkip(10)} disabled={isLoading || mode !== 'tts' || !duration}>
              <Forward10Icon fontSize="small" />
            </IconButton>
          </Box>
          <Typography component="span">{formatTime(duration)}</Typography>
        </Box>
      </Box>
      {error && <Typography className="storybook-3d-reader__narration-error" role="alert">{error}</Typography>}
    </Box>
  );
});

const TtsNarrationController = forwardRef(function TtsNarrationController(props, ref) {
  const { getToken } = useAuth();
  const { user } = useUser();
  return <NarrationCore {...props} getToken={getToken} userId={user?.id} ref={ref} />;
});

const StoryNarrationController = forwardRef(function StoryNarrationController(props, ref) {
  if (props.mode === 'tts') return <TtsNarrationController {...props} ref={ref} />;
  return <NarrationCore {...props} mode="browser" ref={ref} />;
});

export default StoryNarrationController;
